function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function isPigmentJsonValue(value, ancestors = new Set()) {
    if (value === null || typeof value === "string" || typeof value === "boolean")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value);
    if (Array.isArray(value)) {
        if (ancestors.has(value))
            return false;
        ancestors.add(value);
        const valid = value.every((item) => isPigmentJsonValue(item, ancestors));
        ancestors.delete(value);
        return valid;
    }
    if (isRecord(value)) {
        const prototype = Object.getPrototypeOf(value);
        if (prototype !== Object.prototype && prototype !== null)
            return false;
        if (ancestors.has(value))
            return false;
        ancestors.add(value);
        const valid = Object.values(value).every((item) => isPigmentJsonValue(item, ancestors));
        ancestors.delete(value);
        return valid;
    }
    return false;
}
function isStableIdentifier(value) {
    return typeof value === "string" && value.length > 0 && value === value.trim();
}
function isBlockNodeWithAncestors(value, ancestors) {
    if (!isRecord(value) || ancestors.has(value))
        return false;
    if (typeof value.id !== "string" || typeof value.type !== "string")
        return false;
    if (value.slot !== undefined && typeof value.slot !== "string")
        return false;
    if (value.props !== undefined && !isRecord(value.props))
        return false;
    ancestors.add(value);
    const validChildren = value.children === undefined
        || (Array.isArray(value.children) && value.children.every((child) => isBlockNodeWithAncestors(child, ancestors)));
    ancestors.delete(value);
    return validChildren;
}
export function isBlockNode(value) {
    return isBlockNodeWithAncestors(value, new Set());
}
export function isBlockNodeArray(value) {
    return Array.isArray(value) && value.every(isBlockNode);
}
/**
 * A document mutation addresses blocks by ID, so an ID must identify exactly
 * one node across a tree. This remains public for hosts that compose several
 * valid roots before placing them in a document.
 */
export function hasUniqueBlockIds(nodes) {
    const ids = new Set();
    const visit = (node) => {
        if (!isStableIdentifier(node.id) || ids.has(node.id))
            return false;
        ids.add(node.id);
        return node.children?.every(visit) ?? true;
    };
    return nodes.every(visit);
}
export function findBlock(nodes, id) {
    for (const node of nodes) {
        if (node.id === id)
            return node;
        if (node.children) {
            const hit = findBlock(node.children, id);
            if (hit)
                return hit;
        }
    }
    return null;
}
export function replaceBlock(nodes, id, next) {
    const out = [];
    for (const node of nodes) {
        if (node.id === id) {
            if (next)
                out.push(next);
            continue;
        }
        out.push(node.children ? { ...node, children: replaceBlock(node.children, id, next) } : node);
    }
    return out;
}
export function patchBlockProps(nodes, id, props) {
    const target = findBlock(nodes, id);
    if (!target)
        return nodes;
    return replaceBlock(nodes, id, { ...target, props: { ...(target.props ?? {}), ...props } });
}
export function resetBlockProp(nodes, id, key) {
    const target = findBlock(nodes, id);
    if (!target?.props || !(key in target.props))
        return nodes;
    const nextProps = { ...target.props };
    delete nextProps[key];
    return replaceBlock(nodes, id, { ...target, props: nextProps });
}
export function insertBlock(nodes, parentId, child, index) {
    if (parentId === null) {
        const out = [...nodes];
        out.splice(index ?? out.length, 0, child);
        return out;
    }
    return nodes.map((node) => {
        if (node.id === parentId) {
            const children = [...(node.children ?? [])];
            children.splice(index ?? children.length, 0, child);
            return { ...node, children };
        }
        return node.children ? { ...node, children: insertBlock(node.children, parentId, child, index) } : node;
    });
}
export function moveBlock(nodes, id, delta) {
    const index = nodes.findIndex((node) => node.id === id);
    if (index !== -1) {
        const destination = index + delta;
        if (destination < 0 || destination >= nodes.length)
            return nodes;
        const out = [...nodes];
        const [moved] = out.splice(index, 1);
        out.splice(destination, 0, moved);
        return out;
    }
    return nodes.map((node) => (node.children ? { ...node, children: moveBlock(node.children, id, delta) } : node));
}
export function parentIdOf(nodes, id, parent = null) {
    for (const node of nodes) {
        if (node.id === id)
            return parent;
        if (node.children) {
            const hit = parentIdOf(node.children, id, node.id);
            if (hit !== undefined)
                return hit;
        }
    }
    return undefined;
}
